import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import Breakpoints from "common/breakpoints";
import AppPageHeader from "components/AppPageHeader";
import AppSearch from "components/AppSearch/AppSearch";
import DateRangeSelect from "components/DateRangeSelect";
import { getDateRangeForStore } from "components/DateRangeSelect/helpers";
import ErrorBoundary from "components/ErrorBoundary";
import FacilitySelect from "components/FacilitySelect/FacilitySelect";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import DateRange, { DateRangeType } from "interfaces/DateRange";
import { useTrackOpticsStore } from "pages/TrackOpticsPage/useTrackOpticsStore";
import {
  setSelectedDateRange,
  setSelectedFacility
} from "store/actions/trackOpticsPage";
import { TSupportedEntity } from "utils/app-search-utils";
import {
  FACILITY_GULFSTREAM_PARK,
  TRAINING_CENTER_PALM_MEADOWS
} from "utils/facilities";
import { ETouchMoveListenerAction, setupTouchMoveListener } from "../helpers";

import useStyles from "./styles";

interface IProps {
  onItemClick: (value: TSupportedEntity) => void;
  onSearch: (value: string, item?: TSupportedEntity) => void;
  clearSearch: boolean;
}

const SEARCH_BOX_PLACEHOLDER = "Search Horses";

const dateOptions = [
  DateRangeType.AllDates,
  DateRangeType.Today,
  DateRangeType.Yesterday,
  DateRangeType.Last3Days,
  DateRangeType.Custom
];

const portableOption = {
  id: "0306d136-c97b-4e13-8c26-f3c21346dce7", //provide random ID to get no videos in response
  name: "Portable",
  code: "PORTABLE",
  backgroundColor: "000000"
};

const TrackOpticsPageHeader = (props: IProps) => {
  const { onItemClick, onSearch, clearSearch } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const matchesDownSM600 = useMediaQuery(
    theme.breakpoints.down(Breakpoints.SM_600)
  );

  const [customFacilities, setCustomFacilities] =
    useState<racehorse360.IFacility[]>(null);

  const { selectedFacility, convertedToDateSelectedDateRange } =
    useTrackOpticsStore();
  const { currentUser } = useLoggedInUser();

  const shouldDisplayAllTracksOption: boolean =
    currentUser.vetFacilityIds.length > 1;
  const shouldDisplayPalmMeadowsOption = [
    currentUser.isVeterinarian,
    currentUser.facilityIdsListFromRoles.includes(FACILITY_GULFSTREAM_PARK)
  ].every(Boolean);

  const { useGetFacility } = useRacehorse360Api();

  const { data: palmMeadowsFacility } = useGetFacility({
    id: TRAINING_CENTER_PALM_MEADOWS,
    getOptions: {
      select: [
        "name",
        "code",
        "backgroundColor",
        "foregroundColor",
        "strokeColor"
      ]
    }
  });

  const handleDateRangeChange = useCallback(
    (dateRange: DateRange) => {
      const storeDateRange = getDateRangeForStore(dateRange);

      dispatch(setSelectedDateRange(storeDateRange));
    },
    [dispatch]
  );

  const handleFacilityChange = useCallback(
    (facility: racehorse360.IFacility) => {
      dispatch(setSelectedFacility(facility));
    },
    [dispatch]
  );

  const handleFocusInput = useCallback(
    e => setupTouchMoveListener(e.target, ETouchMoveListenerAction.ADD),
    [setupTouchMoveListener]
  );

  const handleBlurInput = useCallback(
    e => setupTouchMoveListener(e.target, ETouchMoveListenerAction.REMOVE),
    [setupTouchMoveListener]
  );

  useEffect(() => {
    let newCustomFacilities: racehorse360.IFacility[];

    if (palmMeadowsFacility) {
      if (shouldDisplayPalmMeadowsOption && currentUser.isChiefVet) {
        newCustomFacilities = [portableOption, palmMeadowsFacility];
      } else if (shouldDisplayPalmMeadowsOption) {
        newCustomFacilities = [palmMeadowsFacility];
      } else if (currentUser.isChiefVet) {
        newCustomFacilities = [portableOption];
      }
    } else {
      newCustomFacilities = currentUser.isChiefVet ? [portableOption] : null;
    }

    setCustomFacilities(newCustomFacilities);
  }, [shouldDisplayPalmMeadowsOption, currentUser, palmMeadowsFacility]);

  return (
    <AppPageHeader className={classes.pageHeader}>
      <div className={classes.internalPageHeaderContainer}>
        <div className={classes.facilityContainer}>
          <ErrorBoundary>
            <FacilitySelect
              isActive={true}
              selectedFacility={selectedFacility}
              onChange={handleFacilityChange}
              shouldDisplayAllTracksOption={shouldDisplayAllTracksOption}
              customOptions={customFacilities}
              shouldDisplayTrainingFacilities={true}
            />
          </ErrorBoundary>
        </div>

        {!matchesDownSM600 && (
          <AppSearch
            className={classes.search}
            placeholder={SEARCH_BOX_PLACEHOLDER}
            onItemClick={onItemClick}
            onSearch={onSearch}
            clearSearch={clearSearch}
          />
        )}

        <div className={classes.allDatesContainer}>
          <ErrorBoundary>
            <DateRangeSelect
              selectedDateRange={convertedToDateSelectedDateRange}
              onChange={handleDateRangeChange}
              defaultSelectedDateRange={DateRangeType.Today}
              dateOptions={dateOptions}
              maxDate={new Date()}
            />
          </ErrorBoundary>
        </div>
      </div>

      {matchesDownSM600 && (
        <AppSearch
          className={classes.mobileViewSearch}
          placeholder={SEARCH_BOX_PLACEHOLDER}
          onItemClick={onItemClick}
          onSearch={onSearch}
          clearSearch={clearSearch}
          onFocus={handleFocusInput}
          onBlur={handleBlurInput}
        />
      )}
    </AppPageHeader>
  );
};

export default React.memo(TrackOpticsPageHeader);
